/**
 * Экран матрицы приоритетов перекрытия — визуальная настройка процента
 * A-Book (BRD §4; техспека BL-2026-10-09-4f80 §3.2, приёмка 2 и 3).
 *
 * Flow: монтирование → три независимых запроса (GET /api/dealing/coverage-rules
 *       — действующие правила, GET /api/dealing/filters — список групп,
 *       GET /api/dealing/symbols — каталог универсальных символов) → человек
 *       спрашивает разрешение «что применится логину X по символу Y»
 *       (GET /api/dealing/coverage-resolve) и видит победившее правило вместе
 *       с проигравшими → правит правила трёх уровней → перед сохранением
 *       видит перечень смен, которые уйдут в ИСТОРИЮ снепшотами →
 *       PUT /api/dealing/coverage-rules записывает матрицу целиком.
 *
 * Зависимости:
 *   - react: состояние, жизненный цикл, мемоизация разбора черновика;
 *   - ./api: loadDealingCoverageRules, saveDealingCoverageRules,
 *     loadDealingFilters (группы), loadDealingSymbols (универсальные
 *     символы), resolveDealingCoverage (разрешение иерархии).
 *
 * Связанные файлы:
 *   - src/DealingDeskPanel.jsx — вкладка «Матрица перекрытия» раздела;
 *   - src/CoverageMatrixPanel.test.jsx — проверки приёмки 2 и 3;
 *   - docs/specs/BL-2026-10-09-4f80-dealing-completion.md — §3.2 экран,
 *     §4 контракт двух новых ручек, §5 приёмка;
 *   - docs/specs/BL-2026-09-16-90e6-dealing-desk.md — §4 матрица
 *     приоритетов, Приложение А — контракт GET/PUT coverage-rules.
 *
 * # ПОЧЕМУ ЭКРАН НЕСЁТ РАЗРЕШЕНИЕ, А НЕ ТОЛЬКО ТАБЛИЦУ ПРАВИЛ
 *
 * Правила трёх уровней, показанные тремя списками, не отвечают на вопрос,
 * который человек задаёт ПЕРЕД правкой: «что сейчас применится к этому
 * логину по этому символу». Ответ на него читатель выводил бы сам — сверяя
 * логин со списком уровня 1, его группу со списком уровня 2 и с базовым
 * групповым, — то есть выполнял бы разрешение иерархии в уме, на каждом
 * вопросе заново. Там, где иерархия сложнее всего (правило уровня 1 с
 * выключенным тумблером, перебивающее групповое по символу), он ошибётся,
 * и ошибка проявится не на экране, а в раскладке витрины через сутки.
 *
 * Поэтому «строгая иерархия» здесь — свойство интерфейса, а не текст в ТЗ:
 * блок разрешения стоит ПЕРВЫМ, до списков правил, и показывает не только
 * победителя, но и проигравших с названной причиной.
 *
 * # ПОЧЕМУ ПРОЦЕНТ — СТРОКА И НЕ ПРОХОДИТ ЧЕРЕЗ Number
 *
 * Та же причина, что у денежных полей витрины: значение приходит строкой и
 * уходит строкой. Арифметика ниже (дополнение до ста, сравнение «изменилось
 * ли») идёт в ЦЕЛЫХ единицах 1e-6 процента — там double точен, потому что
 * все величины целые и меньше 1e8, то есть много меньше 2^53. Ни одно
 * значение не превращается в дробное число ни на шаг.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  loadDealingCoverageRules,
  loadDealingFilters,
  loadDealingSymbols,
  resolveDealingCoverage,
  saveDealingCoverageRules,
} from "./api";

/**
 * Три уровня матрицы в порядке «кто кого перебивает» (BRD §4).
 *
 * Объявлены ДАННЫМИ, а не тремя кусками разметки: порядок приоритетов —
 * предмет этого экрана, и три копии разметки разошлись бы именно в нём.
 * Тот же приём, что у состава столбцов витрины (DEALING_COLUMNS).
 *
 * Поля:
 *   priority    — уровень, он же значение поля priority контракта;
 *   title       — заголовок блока;
 *   beats       — место уровня в иерархии словами;
 *   scope       — чем задаётся область действия: 'login' либо 'group';
 *   hasSymbol   — есть ли у уровня символ и тумблер «по символу»;
 *   offMeans    — что означает выключенный тумблер (BRD §4, «Логика»).
 */
export const COVERAGE_LEVELS = [
  {
    priority: 1,
    title: "Приоритет 1 — Логин + универсальный символ",
    beats:
      "перебивает всё: и групповое правило по символу, и базовое групповое",
    scope: "login",
    hasSymbol: true,
    offMeans: "тумблер выключен — процент применяется на весь логин целиком",
  },
  {
    priority: 2,
    title: "Приоритет 2 — Группа + универсальный символ",
    beats:
      "перебивается персональным правилом логина, сам перебивает базовое групповое",
    scope: "group",
    hasSymbol: true,
    offMeans: "тумблер выключен — процент применяется на всю группу целиком",
  },
  {
    priority: 3,
    title: "Приоритет 3 — Базовый групповой",
    beats: "действует, когда правил уровней 1 и 2 для счёта нет",
    scope: "group",
    hasSymbol: false,
    offMeans:
      "символа у базового правила нет вовсе: это умолчание всей торговой группы",
  },
];

/** Процент: до трёх цифр целой части и до шести знаков после запятой. */
const PERCENT_RE = /^(\d{1,3})(?:[.,](\d{1,6}))?$/;

/**
 * Переводит процент из записи человека в ЦЕЛЫЕ единицы 1e-6 процента.
 *
 * @param {string|number|null} text — «80», «80.5», «12,345678»
 * @returns {number|null} целое 0…100000000 либо null, если это не процент
 *          диапазона [0; 100]
 *
 * Единицы выбраны те же, в которых считает сервер (ParsePercent): 1e-6 %
 * равно 1e-8 доли, и перевод «процент → доля» обходится без деления. Здесь
 * они нужны ради двух операций, которые нельзя делать над строкой, —
 * сравнения «изменилось ли правило» и дополнения до ста.
 *
 * Запятая принимается наравне с точкой: цифровая клавиатура в русской
 * раскладке даёт запятую, и отказ «это не число» на «80,5» выглядел бы
 * придиркой, а не подсказкой.
 */
export function percentUnits(text) {
  const m = PERCENT_RE.exec(String(text ?? "").trim());
  if (!m) return null;
  // Обе части — целые: до 100 000 000, то есть вчетверо меньше 2^53.
  const units = Number(m[1]) * 1000000 + Number((m[2] || "").padEnd(6, "0"));
  return units > 100000000 ? null : units;
}

/**
 * Печатает целые единицы 1e-6 процента десятичной записью.
 *
 * @param {number} units — целое 0…100000000
 * @returns {string} «80.00», «12.345678»
 *
 * Два знака — минимум, лишние нули справа отбрасываются: контракт отдаёт
 * «80.00», и отправлять «80.000000» значило бы показывать точность, которой
 * в решении человека нет.
 */
function unitsToPercent(units) {
  const whole = Math.trunc(units / 1000000);
  let frac = String(units % 1000000)
    .padStart(6, "0")
    .replace(/0+$/, "");
  while (frac.length < 2) frac += "0";
  return `${whole}.${frac}`;
}

/**
 * Приводит введённый процент к виду контракта.
 *
 * @param {string|number|null} text — то, что ввёл человек
 * @returns {string|null} «80.00» либо null для непроцента
 */
export function normalizePercent(text) {
  const units = percentUnits(text);
  return units === null ? null : unitsToPercent(units);
}

/**
 * Дополнение процента A-Book до ста — доля B-Book.
 *
 * @param {string|number|null} text — процент A-Book
 * @returns {string|null} процент B-Book либо null
 *
 * Показывается рядом с введённым значением намеренно. «80 % A-Book» и
 * «20 % B-Book» — одно и то же решение, но дилинг формулирует его и так, и
 * так (BRD §4: «вся группа находится в 100% B-Book» — это a_book_pct = 0),
 * и человек, вводящий процент, обязан видеть вторую половину своего
 * решения, а не держать её в уме.
 */
export function bBookPercent(text) {
  const units = percentUnits(text);
  return units === null ? null : unitsToPercent(100000000 - units);
}

/** Значение поля ввода без пробелов по краям; null и undefined — пустая строка. */
function trimmed(value) {
  return String(value ?? "").trim();
}

/** Значение как есть строкой; null и undefined — пустая строка. */
function textOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

/**
 * Область действия правила строкой — ключ, по которому сервер опознаёт
 * правило при правке списка целиком.
 *
 * @param {object} rule — правило либо черновик
 * @returns {string} ключ вида «1|900518||XAUUSD»
 *
 * Ключ — именно ОБЛАСТЬ ДЕЙСТВИЯ, а не идентификатор строки, и это повторяет
 * устройство сервера (dealingRuleKey) и частичные уникальные индексы схемы.
 * Причина в том, что идентификатора у нового правила ещё нет, а «правило на
 * эту группу и этот символ» — то, что человек видит на экране.
 *
 * Тумблер «по символу» в ключ НЕ входит: выключенный тумблер не делает
 * правило другим правилом, он меняет область его действия внутри того же
 * сочетания «логин/группа + символ».
 */
export function scopeKey(rule) {
  return [
    rule.priority,
    trimmed(rule.login),
    trimmed(rule.group),
    trimmed(rule.symbol),
  ].join("|");
}

/**
 * Область действия правила человеческими словами.
 *
 * @param {object} rule — правило либо черновик
 * @returns {string} «логин 900518 целиком (символ XAUUSD сохранён, но не действует)»
 *
 * Выключенный тумблер проговаривается ЦЕЛИКОМ, а не показывается серой
 * галочкой: «символ сохранён, но не действует» — это два разных факта о
 * правиле, и ни один из них не следует из снятой галочки сам собой.
 */
export function scopeText(rule) {
  // Уровень может оказаться неизвестным: priority приходит с сервера, и
  // правило уровня, которого этот бандл не знает, описывается группой —
  // единственным полем, общим для всех уровней, кроме первого.
  const уровень = COVERAGE_LEVELS.find((l) => l.priority === rule.priority);
  const поЛогину = уровень ? уровень.scope === "login" : false;
  const кто = поЛогину
    ? `логин ${trimmed(rule.login) || "—"}`
    : `группа ${trimmed(rule.group) || "—"}`;

  if (!уровень || !уровень.hasSymbol)
    return `${кто} — базовое умолчание группы`;

  const символ = trimmed(rule.symbol);
  if (!символ) return `${кто} целиком, без разбивки по символам`;
  if (rule.symbol_enabled) return `${кто}, символ ${символ}`;
  return `${кто} целиком (символ ${символ} сохранён, но не действует)`;
}

/**
 * Проверяет правило так же, как это сделает сервер (CheckCoveragePriority).
 *
 * @param {object} draft — черновик правила
 * @returns {string} текст беды либо пустая строка
 *
 * Повтор серверной проверки здесь НЕ дублирование решения, а перенос отказа
 * к месту ввода: правило уровня 1 без логина сервер отвергнет целым телом, и
 * человек получит отказ на всю матрицу вместо подсветки одной строки. Текст
 * отказа при этом остаётся за сервером: всё, что он отверг, показывается его
 * словами (см. saveError), а здесь — только то, что видно до отправки.
 */
export function checkRule(draft) {
  if (draft.priority === 1 && !trimmed(draft.login)) {
    return "правило уровня 1 задаётся логином";
  }
  if (draft.priority !== 1 && !trimmed(draft.group)) {
    return `правило уровня ${draft.priority} задаётся группой`;
  }
  if (draft.symbol_enabled && !trimmed(draft.symbol)) {
    return "тумблер «по символу» включён, а символ не задан";
  }
  if (percentUnits(draft.a_book_pct) === null) {
    return "процент A-Book: число от 0 до 100";
  }
  return "";
}

/**
 * Собирает тело PUT /api/dealing/coverage-rules из черновиков.
 *
 * @param {Array<object>} drafts — строки экрана
 * @returns {Array<object>} правила в виде контракта (Приложение А)
 *
 * Что уходит и чего НЕ уходит:
 *   - `login` — СТРОКОЙ: внешний логин платформы это идентификатор, и
 *     сервер ждёт строку (domain.DealingCoverageRule.Login — *string);
 *   - `a_book_pct` — ПРОЦЕНТОМ («80.00»), а не долей 0..1: перевод в долю
 *     делает сервер, и доля «0.8» была бы принята как 0,8 процента;
 *   - поля, которых у уровня нет, уходят null (группа у уровня 1, символ у
 *     уровня 3) — не пустой строкой: пустая строка прошла бы CHECK схемы
 *     как заданное значение;
 *   - `id` НЕ отправляется вовсе: сервер опознаёт правило по области
 *     действия, а не по идентификатору, и присланный id нового правила был
 *     бы выдумкой клиента.
 *
 * Символ при выключенном тумблере СОХРАНЯЕТСЯ в теле: схема различает
 * «символа нет» и «символ есть, но сейчас не действует» (миграция 051,
 * symbol_enabled), и стирать введённое человеком при выключении значило бы
 * терять его работу.
 */
export function coverageRulesPayload(drafts) {
  return drafts.map((d) => {
    const символ = trimmed(d.symbol);
    const естьСимвол = d.priority !== 3 && символ !== "";
    return {
      priority: d.priority,
      login: d.priority === 1 ? trimmed(d.login) : null,
      group: d.priority === 1 ? null : trimmed(d.group) || null,
      symbol: естьСимвол ? символ : null,
      symbol_enabled: естьСимвол ? Boolean(d.symbol_enabled) : false,
      a_book_pct: normalizePercent(d.a_book_pct) ?? trimmed(d.a_book_pct),
    };
  });
}

/**
 * Области действия, заданные дважды.
 *
 * @param {Array<object>} drafts — строки экрана
 * @returns {Array<string>} пояснения-дубликаты в порядке появления
 *
 * Сервер отвергнет такое тело целиком («правило N повторяет область
 * действия предыдущего»), и до отправки это видно: два правила на один
 * логин и один символ — это не «последнее побеждает», а неопределённость,
 * которой в матрице приоритетов быть не может.
 */
export function duplicateScopes(drafts) {
  const видели = new Set();
  const повторы = [];
  for (const d of drafts) {
    const ключ = scopeKey(d);
    if (видели.has(ключ)) повторы.push(scopeText(d));
    else видели.add(ключ);
  }
  return повторы;
}

/**
 * Смены, которые уйдут в ИСТОРИЮ снепшотами при сохранении.
 *
 * @param {Array<object>} serverRules — матрица, как её отдал сервер
 * @param {Array<object>} drafts — черновик экрана
 * @returns {Array<{kind: string, scope: string, from: string|null, to: string|null}>}
 *          kind: 'added' | 'changed' | 'removed'
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ ПОКАЗЫВАТЬ. Правка матрицы пишет снепшот перекрытия той
 * же транзакцией, и из снепшотов потом считается РЕТРОСПЕКТИВНАЯ раскладка
 * (§5.1 BRD): смена доли закрывает один интервал и открывает другой. То
 * есть правка меняет не сегодняшнюю цифру, а историю расчёта, и обнаружится
 * ошибка не на этом экране, а в прошлых периодах витрины. Перечень смен
 * перед сохранением — единственное место, где это ещё можно заметить.
 *
 * СНЯТОЕ правило тоже попадает в перечень и тоже даёт снепшот: после него
 * начинает действовать уровень ниже, и история обязана отметить момент,
 * когда персональная настройка перестала действовать.
 *
 * Сохранение БЕЗ фактических изменений снепшотов не даёт (так устроен
 * сервер), поэтому пустой перечень здесь означает «истории эта правка не
 * коснётся» — и это ровно то, что человеку нужно знать.
 */
export function pendingSnapshots(serverRules, drafts) {
  const было = new Map();
  for (const r of serverRules || []) было.set(scopeKey(r), r);

  const смены = [];
  const осталось = new Set();
  for (const d of drafts) {
    const ключ = scopeKey(d);
    осталось.add(ключ);
    const прежнее = было.get(ключ);
    const стало = normalizePercent(d.a_book_pct);
    if (!прежнее) {
      смены.push({ kind: "added", scope: scopeText(d), from: null, to: стало });
      continue;
    }
    if (percentUnits(прежнее.a_book_pct) !== percentUnits(d.a_book_pct)) {
      смены.push({
        kind: "changed",
        scope: scopeText(d),
        from: normalizePercent(прежнее.a_book_pct),
        to: стало,
      });
    }
  }

  for (const [ключ, r] of было) {
    if (осталось.has(ключ)) continue;
    смены.push({
      kind: "removed",
      scope: scopeText(r),
      from: normalizePercent(r.a_book_pct),
      to: null,
    });
  }
  return смены;
}

/** Как называется смена в перечне перед сохранением. */
const SNAPSHOT_KINDS = {
  added: "правило заведено",
  changed: "процент изменён",
  removed: "правило снято — дальше действует уровень ниже",
};

/**
 * Исход правила в разрешении иерархии.
 *
 * @param {object} entry — элемент considered ответа coverage-resolve
 * @param {object|null} effective — поле effective того же ответа
 * @returns {{kind: string, label: string, why: string}} kind:
 *          'win' (применено) | 'lost' (перебито) | 'miss' (не сработало)
 *
 * ПРОИГРАВШИЕ ПОКАЗЫВАЮТСЯ НАРАВНЕ С ПОБЕДИТЕЛЕМ. Один победитель отвечает
 * на вопрос «сколько процентов», но не на вопрос «почему не то правило,
 * которое я только что завёл» — а задаётся как раз второй. Причина берётся
 * из поля `why` сервера; своей причины здесь не выдумывается, и при её
 * отсутствии пишется то немногое, что известно наверняка из самого исхода.
 */
export function resolveVerdict(entry, effective) {
  if (!entry.matched) {
    return {
      kind: "miss",
      label: "не сработало",
      why: entry.why || "правила этого уровня для счёта нет",
    };
  }
  const победил =
    effective &&
    effective.source === "rule" &&
    effective.priority === entry.priority;
  if (победил) {
    return {
      kind: "win",
      label: "ПРИМЕНЕНО",
      why: entry.why || "правило наивысшего сработавшего уровня",
    };
  }
  return {
    kind: "lost",
    label: "перебито",
    why:
      entry.why ||
      (effective
        ? `перебито правилом приоритета ${effective.priority}`
        : "перебито правилом выше"),
  };
}

/** Откуда взялась действующая доля в разрешении (то же поле, что у витрины). */
const EFFECTIVE_SOURCES = {
  rule: "по действующему правилу матрицы",
  snapshot: "по накопленной истории интервалов",
  default:
    "ни одно правило не сработало: применено умолчание BRD §5.3 — вся " +
    "группа в B-Book, разбивки по символам внутри логина нет",
};

// ---------------------------------------------------------------------------
// Блок разрешения иерархии
// ---------------------------------------------------------------------------

/**
 * Разрешение: что применится к логину по символу и почему.
 *
 * @param {Array<string>} symbols — коды универсальных символов для списка
 * @param {boolean} symbolsFree — список символов недоступен, вводим текстом
 *
 * Побочный эффект: GET /api/dealing/coverage-resolve по нажатию, а не по
 * вводу. Запрос на каждую нажатую клавишу означал бы разрешение иерархии на
 * сервере столько раз, сколько цифр в логине, — и три из них отменёнными.
 */
function ResolveBlock({ symbols, symbolsFree }) {
  const [login, setLogin] = useState("");
  const [symbol, setSymbol] = useState("");
  const [state, setState] = useState({ status: "idle" });

  const спросить = (event) => {
    event.preventDefault();
    const счёт = trimmed(login);
    if (!счёт) return;
    setState({ status: "loading" });
    resolveDealingCoverage(счёт, trimmed(symbol))
      .then((data) => setState({ status: "ready", data }))
      .catch((e) => setState({ status: "error", message: e.message }));
  };

  return (
    <section className="block">
      <h3>Что применится: разрешение иерархии</h3>
      <p className="block-legend">
        Три уровня правил не отвечают на вопрос, который задают перед правкой:
        «какой процент получит ЭТОТ счёт по ЭТОМУ символу». Здесь виден ответ
        целиком — и правило, которое победило, и те, что проиграли, с причиной.
        Символ можно не указывать: тогда вопрос звучит «что применится к логину
        целиком, без разбивки по символам».
      </p>

      <form className="resolve-ask" onSubmit={спросить}>
        <label className="filter">
          <span>Логин</span>
          <input
            type="text"
            inputMode="numeric"
            aria-label="Логин для разрешения"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
          />
        </label>
        <label className="filter">
          <span>Универсальный символ</span>
          {symbolsFree ? (
            <input
              type="text"
              aria-label="Символ для разрешения"
              placeholder="без символа"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
            />
          ) : (
            <select
              aria-label="Символ для разрешения"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
            >
              <option value="">— без символа —</option>
              {symbols.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          )}
        </label>
        <div className="filter filter-checks">
          <button type="submit" disabled={!trimmed(login)}>
            Показать, что применится
          </button>
        </div>
      </form>

      {state.status === "loading" && <p className="muted">Разрешаем…</p>}
      {state.status === "error" && (
        <p className="error">Разрешение не получено: {state.message}</p>
      )}
      {state.status === "ready" && <ResolveResult data={state.data} />}
    </section>
  );
}

/**
 * Ответ разрешения: действующая доля сверху, разбор по уровням под ней.
 *
 * @param {object} data — ответ GET /api/dealing/coverage-resolve
 *
 * Порядок строк — порядок ПРИОРИТЕТОВ, а не порядок ответа: иерархия
 * читается сверху вниз, и перестановка уровней сервером (он вправе вернуть
 * их как угодно) сделала бы экран нечитаемым именно в том, ради чего он
 * заведён.
 */
function ResolveResult({ data }) {
  const effective = data.effective || null;
  const considered = [...(data.considered || [])].sort(
    (a, b) => a.priority - b.priority,
  );
  const a = effective ? normalizePercent(effective.a_book_pct) : null;
  const b = effective ? bBookPercent(effective.a_book_pct) : null;

  return (
    <>
      <p className="resolve-effective">
        <strong>
          Счёт {data.login}
          {data.symbol
            ? `, символ ${data.symbol}`
            : ", без разбивки по символам"}
          : A-Book {a ?? "—"} % · B-Book {b ?? "—"} %
        </strong>
        <span className="muted">
          {" "}
          —{" "}
          {EFFECTIVE_SOURCES[effective && effective.source] ||
            "источник доли не назван"}
          {effective && effective.source === "rule"
            ? `, приоритет ${effective.priority}`
            : ""}
        </span>
      </p>

      <table className="resolve-table">
        <thead>
          <tr>
            <th>Приоритет</th>
            <th>Область правила</th>
            <th className="num">A-Book</th>
            <th>Исход</th>
            <th>Почему</th>
          </tr>
        </thead>
        <tbody>
          {considered.map((entry) => {
            const исход = resolveVerdict(entry, effective);
            return (
              <tr key={entry.priority} className={`verdict-${исход.kind}`}>
                <th scope="row">{entry.priority}</th>
                <td>{entry.scope}</td>
                <td className="num">
                  {entry.a_book_pct
                    ? `${normalizePercent(entry.a_book_pct) ?? entry.a_book_pct} %`
                    : "—"}
                </td>
                <td>
                  <span className={`verdict-mark verdict-${исход.kind}-mark`}>
                    {исход.label}
                  </span>
                </td>
                <td className="muted">{исход.why}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {considered.length === 0 && (
        <p className="muted">
          Сервер не перечислил рассмотренные правила. Без перечня видна только
          действующая доля, а почему она такая — нет.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Правила уровня
// ---------------------------------------------------------------------------

/**
 * Одна строка правила.
 *
 * @param {object} draft — черновик правила
 * @param {object} level — элемент COVERAGE_LEVELS
 * @param {Array<string>} groups — торговые группы из GET /api/dealing/filters
 * @param {Array<string>} symbols — коды универсальных символов
 * @param {boolean} symbolsFree — каталог символов недоступен, вводим текстом
 * @param {boolean} canEdit — права на правку (запись открыта администратору)
 * @param {function} onChange — принимает частичную правку черновика
 * @param {function} onRemove — снимает правило из черновика
 */
function RuleRow({
  draft,
  level,
  groups,
  symbols,
  symbolsFree,
  canEdit,
  onChange,
  onRemove,
}) {
  const беда = checkRule(draft);
  const b = bBookPercent(draft.a_book_pct);

  return (
    <div className={беда ? "rule-row rule-bad" : "rule-row"}>
      {level.scope === "login" ? (
        <label className="filter">
          <span>Логин</span>
          <input
            type="text"
            inputMode="numeric"
            aria-label="Логин правила"
            disabled={!canEdit}
            value={draft.login || ""}
            onChange={(e) => onChange({ login: e.target.value })}
          />
        </label>
      ) : (
        <label className="filter">
          <span>Торговая группа</span>
          <select
            aria-label="Группа правила"
            disabled={!canEdit}
            value={draft.group || ""}
            onChange={(e) => onChange({ group: e.target.value })}
          >
            <option value="">— выберите группу —</option>
            {/* Группа правила могла исчезнуть из доступа менеджера: её
                значение всё равно показывается, иначе правка соседней
                строки молча переписала бы это правило на пустую группу. */}
            {!groups.includes(draft.group || "") && draft.group ? (
              <option value={draft.group}>{draft.group}</option>
            ) : null}
            {groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
      )}

      {level.hasSymbol && (
        <>
          <label className="filter">
            <span>Универсальный символ</span>
            {symbolsFree ? (
              <input
                type="text"
                aria-label="Символ правила"
                disabled={!canEdit}
                value={draft.symbol || ""}
                onChange={(e) => onChange({ symbol: e.target.value })}
              />
            ) : (
              <select
                aria-label="Символ правила"
                disabled={!canEdit}
                value={draft.symbol || ""}
                onChange={(e) => onChange({ symbol: e.target.value })}
              >
                <option value="">— без символа —</option>
                {!symbols.includes(draft.symbol || "") && draft.symbol ? (
                  <option value={draft.symbol}>{draft.symbol}</option>
                ) : null}
                {symbols.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="filter">
            <span>Режим по символу</span>
            <span className="check">
              <input
                type="checkbox"
                aria-label="По символу"
                disabled={!canEdit}
                checked={Boolean(draft.symbol_enabled)}
                onChange={(e) => onChange({ symbol_enabled: e.target.checked })}
              />
              <span>{draft.symbol_enabled ? "включён" : "выключен"}</span>
            </span>
          </label>
        </>
      )}

      <label className="filter">
        <span>A-Book, %</span>
        <input
          type="text"
          inputMode="decimal"
          aria-label="Процент A-Book"
          disabled={!canEdit}
          value={draft.a_book_pct ?? ""}
          onChange={(e) => onChange({ a_book_pct: e.target.value })}
        />
      </label>

      <div className="filter rule-aside">
        <span className="muted">{scopeText(draft)}</span>
        <span className="muted">
          {b === null ? "процент не разобран" : `B-Book ${b} %`}
          {draft.updated_by
            ? ` · правил ${draft.updated_by}`
            : " · правило ещё не сохранено"}
        </span>
        {беда && <span className="error-text">{беда}</span>}
      </div>

      <div className="filter filter-checks">
        <button type="button" disabled={!canEdit} onClick={onRemove}>
          Снять правило
        </button>
      </div>
    </div>
  );
}

/**
 * Блок одного уровня приоритета: пояснение, строки правил, кнопка «добавить».
 *
 * @param {object} level — элемент COVERAGE_LEVELS
 * @param {Array<object>} drafts — ВСЕ черновики (строки этого уровня отбираются здесь)
 * @param {object} rest — остальные свойства строки (см. RuleRow)
 *
 * Уровни показаны отдельными блоками, а не одной таблицей со столбцом
 * «приоритет». Иерархия — главное свойство матрицы, и в плоской таблице она
 * становится ещё одним столбцом, по которому надо отсортировать глазами.
 */
function LevelBlock({ level, drafts, canEdit, onAdd, children }) {
  const свои = drafts.filter((d) => d.priority === level.priority);
  return (
    <section className="block coverage-level">
      <h3>{level.title}</h3>
      <p className="block-legend">
        {level.beats}. {level.offMeans}.
      </p>
      {свои.length === 0 ? (
        <p className="muted">
          Правил этого уровня нет. Это не пробел: уровень просто не участвует в
          разрешении, и действует ближайший сработавший уровень ниже.
        </p>
      ) : (
        children
      )}
      <div className="inline">
        <button type="button" disabled={!canEdit} onClick={onAdd}>
          Добавить правило приоритета {level.priority}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Экран целиком
// ---------------------------------------------------------------------------

/** Счётчик ключей строк: идентификатора у нового правила ещё нет. */
let следующийКлюч = 0;

/**
 * Превращает правило сервера в строку экрана.
 *
 * @param {object} rule — правило из GET /api/dealing/coverage-rules
 * @returns {object} черновик со своим ключом отрисовки
 *
 * Ключ — свой, а не `id` сервера: у новой строки идентификатора нет, а
 * индекс массива в качестве ключа переставил бы содержимое полей при снятии
 * правила из середины (React сопоставил бы строки по позиции).
 */
function ruleToDraft(rule) {
  следующийКлюч += 1;
  return {
    key: `r${следующийКлюч}`,
    priority: rule.priority,
    // Поля, которых у уровня нет, приходят null — в поле ввода им
    // соответствует пустая строка. Без приведения React переключил бы
    // поле из управляемого в неуправляемое и предупредил бы об этом
    // в консоли, а значение при первой же правке соседнего поля пропало.
    login: textOrEmpty(rule.login),
    group: textOrEmpty(rule.group),
    symbol: textOrEmpty(rule.symbol),
    symbol_enabled: Boolean(rule.symbol_enabled),
    a_book_pct:
      normalizePercent(rule.a_book_pct) ?? String(rule.a_book_pct ?? ""),
    updated_by: rule.updated_by || "",
  };
}

/** Пустая строка уровня: процент по умолчанию нулевой — то есть 100 % B-Book. */
function emptyDraft(priority) {
  следующийКлюч += 1;
  return {
    key: `n${следующийКлюч}`,
    priority,
    login: "",
    group: "",
    symbol: "",
    symbol_enabled: false,
    // Ноль, а не пустое поле: умолчание перекрытия — весь объём в B-Book
    // (BRD §4, «вся группа находится в 100% B-Book»), и пустое поле
    // заставило бы человека вводить умолчание руками.
    a_book_pct: "0.00",
    updated_by: "",
  };
}

/** Пустые списки до ответа сервера: экран рисуется сразу, поля — пустыми. */
const EMPTY_SYMBOLS = { symbols: [], unbound: [] };

/**
 * Право правки в ТРЁХ состояниях, а не в двух.
 *
 * @param {object|null} ответ — тело GET /api/dealing/coverage-rules
 * @returns {{canEdit: boolean, unknown: boolean}} canEdit — открыты ли поля;
 *          unknown — сервер о праве не сообщил
 *
 * # ПОЧЕМУ ПРАВО НЕ ВЫВОДИТСЯ ИЗ РОЛИ
 *
 * Роль у экрана есть (её отдаёт GET /api/me), и соблазн написать
 * `role === 'admin'` велик. Это было бы ТРЕТЬИМ местом решения о правах —
 * после предиката CanConfigureDealing в Go и перечня маршрутов. Три копии
 * одного решения расходятся молча и чинятся выкладкой: ровно так навигация
 * однажды обещала роли `dealing` казначейские разделы. Признак обязан
 * приходить оттуда же, откуда приходит отказ, — с сервера.
 *
 * # ПОЧЕМУ ТРЕТЬЕ СОСТОЯНИЕ, А НЕ «ПОЛЯ НЕТ — ЗНАЧИТ НЕЛЬЗЯ»
 *
 * Поле `can_edit` в контракт добавлено (решение заказчика 2026-10-09:
 * правят `admin` и `dealing`, видят все, кому выдан раздел), но выкладка
 * бэкенда и фронтенда может разойтись во времени. Ответ без признака
 * означает ровно одно: ИНТЕРФЕЙС НОВЕЕ СЕРВЕРА — то же переходное
 * состояние, что `SectionUnsupported` в App.jsx, и лечится оно выкладкой,
 * а не выбором за сервер.
 *
 * Запереть экран наглухо на время этого окна значило бы, что задать
 * перекрытие по-прежнему неоткуда, — тот самый дефект, ради которого экран
 * и делается. Поэтому неизвестность — отдельное состояние: поля открыты,
 * но рядом сказано, что сохранение может быть отвергнуто, и отказ будет
 * показан дословно.
 *
 * Чего здесь НЕТ и не будет: перечня ролей, которым правка открыта. Он
 * живёт в предикате CanConfigureDealing, и копия его здесь разошлась бы с
 * оригиналом при первом же изменении состава ролей — молча.
 */
export function editRight(ответ) {
  if (!ответ || ответ.can_edit === undefined || ответ.can_edit === null) {
    return { canEdit: true, unknown: true };
  }
  return { canEdit: ответ.can_edit === true, unknown: false };
}

/**
 * Экран матрицы перекрытия целиком.
 *
 * Свойств у компонента нет намеренно: всё, что ему нужно знать, приходит в
 * ответе сервера. Прежде сюда передавался `user`, и право правки выводилось
 * из названия роли, — см. editRight о том, почему так нельзя.
 *
 * Побочные эффекты: GET /api/dealing/coverage-rules, GET /api/dealing/filters
 * и GET /api/dealing/symbols при монтировании; PUT /api/dealing/coverage-rules
 * по подтверждённому сохранению.
 */
export default function CoverageMatrixPanel() {
  const [server, setServer] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  // Подтверждение: первое нажатие «Сохранить» НЕ сохраняет, а показывает
  // перечень смен, которые уйдут в историю снепшотами.
  const [confirm, setConfirm] = useState(false);

  const [groups, setGroups] = useState([]);
  const [symbols, setSymbols] = useState(EMPTY_SYMBOLS);
  const [symbolsError, setSymbolsError] = useState("");

  /**
   * Раскладывает ответ сервера в состояние экрана и черновик.
   *
   * Сохраняется ВЕСЬ ответ, а не один список правил: право правки приходит
   * тем же телом (см. editRight), и взяв отсюда только правила, экран терял
   * бы признак при каждом сохранении.
   */
  const apply = useCallback((ответ) => {
    setServer(ответ || { rules: [] });
    setDrafts(((ответ && ответ.rules) || []).map(ruleToDraft));
    setConfirm(false);
  }, []);

  useEffect(() => {
    loadDealingCoverageRules()
      .then(apply)
      .catch((e) => setError(e.message));
  }, [apply]);

  useEffect(() => {
    // Группы берутся из той же ручки, что кормит отбор витрины: второй
    // источник перечня групп разошёлся бы с ним молча, и правило завелось
    // бы на группу, которой менеджерский доступ не видит.
    loadDealingFilters()
      .then((data) => setGroups(Array.isArray(data.groups) ? data.groups : []))
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    loadDealingSymbols()
      .then((data) => setSymbols({ ...EMPTY_SYMBOLS, ...data }))
      .catch((e) => setSymbolsError(e.message));
  }, []);

  const коды = useMemo(
    () => (symbols.symbols || []).map((s) => s.code).filter(Boolean),
    [symbols],
  );
  // Каталог символов не отвечает — поле становится текстовым, а не
  // исчезает: без него матрицу нельзя править вовсе, а причина названа
  // отдельной строкой. Экран, который молча убрал бы выбор символа,
  // выглядел бы как экран без правил по символам.
  const symbolsFree = symbolsError !== "" || коды.length === 0;

  const { canEdit, unknown: правоНеизвестно } = editRight(server);

  const смены = useMemo(
    () => pendingSnapshots((server && server.rules) || [], drafts),
    [server, drafts],
  );
  const повторы = useMemo(() => duplicateScopes(drafts), [drafts]);
  const беды = useMemo(
    () => drafts.map(checkRule).filter((m) => m !== ""),
    [drafts],
  );

  if (error) {
    return (
      <section className="block">
        <h2>Матрица перекрытия</h2>
        <p className="error">Матрица не загрузилась: {error}</p>
      </section>
    );
  }

  if (server === null) {
    return (
      <section className="block">
        <h2>Матрица перекрытия</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  /** Правит одну строку черновика. */
  const change = (key, patch) => {
    setNotice("");
    setConfirm(false);
    setDrafts((prev) =>
      prev.map((d) => (d.key === key ? { ...d, ...patch } : d)),
    );
  };

  /** Снимает строку. Снятие — тоже смена доли, и оно попадёт в перечень смен. */
  const remove = (key) => {
    setNotice("");
    setConfirm(false);
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  /** Добавляет пустую строку уровня. */
  const add = (priority) => {
    setNotice("");
    setConfirm(false);
    setDrafts((prev) => [...prev, emptyDraft(priority)]);
  };

  /**
   * Сохраняет матрицу целиком.
   *
   * Первое нажатие НЕ сохраняет: оно показывает перечень смен, которые уйдут
   * в историю снепшотами. Второе — отправляет. Подтверждение стоит здесь не
   * ради осторожности вообще, а потому что предмет правки — ИСТОРИЯ расчёта:
   * по снепшотам считается ретроспективная раскладка, и ошибка проявится в
   * прошлых периодах витрины, а не на этом экране.
   *
   * Побочные эффекты: PUT /api/dealing/coverage-rules (замена матрицы
   * целиком и запись снепшотов одной транзакцией на сервере).
   */
  const save = () => {
    if (беды.length > 0 || повторы.length > 0) {
      setConfirm(false);
      setSaveError("Матрица не отправлена: поправьте отмеченные правила.");
      return;
    }
    if (!confirm) {
      setConfirm(true);
      setSaveError("");
      setNotice("");
      return;
    }
    setSaving(true);
    setSaveError("");
    saveDealingCoverageRules(coverageRulesPayload(drafts))
      .then((ответ) => {
        apply(ответ);
        setNotice(
          "Матрица сохранена. Смены записаны снепшотами той же транзакцией — " +
            "раскладка прошлых периодов будет считаться уже по ним.",
        );
      })
      .catch((e) => setSaveError(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="coverage-matrix">
      <p className="block-legend">
        Процент A-Book задаётся тремя уровнями со строгой иерархией «кто кого
        перебивает» (BRD §4). Берётся ПЕРВОЕ сработавшее правило сверху вниз;
        правило с выключенным тумблером «по символу» срабатывает как правило на
        весь логин или на всю группу. Проверить, что получится, можно, не
        сохраняя, — блоком разрешения ниже.
      </p>

      <ResolveBlock symbols={коды} symbolsFree={symbolsFree} />

      {symbolsError && (
        <p className="notice notice-slim">
          Каталог универсальных символов не загрузился: {symbolsError}. Символ
          вводится текстом — сверяйтесь с написанием сами: сервер отвергнет
          правило на символ, которого в справочнике нет.
        </p>
      )}

      {symbols.unbound && symbols.unbound.length > 0 && (
        <p className="notice notice-slim">
          Суффиксы без универсального символа ({symbols.unbound.length}):{" "}
          {symbols.unbound.join(", ")}. Правило на них не завести, и объём по
          ним не разложится — их надо привязать в справочнике символов.
        </p>
      )}

      {!canEdit && (
        <p className="notice notice-slim">
          Матрица показана только для чтения: сервер не открыл вашей учётной
          записи правку перекрытия. Причина ограничения — в последствиях
          правки: она пишет снепшот перекрытия, из которого считается
          ИСТОРИЧЕСКАЯ раскладка, и ошибка здесь искажает не сегодняшнюю
          цифру, а прошлые. Кому правка открыта, решает сервер — экран этого
          перечня не хранит.
        </p>
      )}

      {правоНеизвестно && (
        <p className="notice notice-slim">
          Сервер не сообщил, доступна ли вам правка матрицы: признака права в
          ответе нет — скорее всего, бэкенд выложен раньше этой страницы.
          Обновите страницу. Поля пока открыты, но сохранение может быть
          отвергнуто — отказ будет показан дословно. Угадывать право по
          названию роли экран не станет: решение о правах принимает сервер,
          и вторая его копия здесь разошлась бы с первой молча.
        </p>
      )}

      {COVERAGE_LEVELS.map((level) => (
        <LevelBlock
          key={level.priority}
          level={level}
          drafts={drafts}
          canEdit={canEdit}
          onAdd={() => add(level.priority)}
        >
          {drafts
            .filter((d) => d.priority === level.priority)
            .map((d) => (
              <RuleRow
                key={d.key}
                draft={d}
                level={level}
                groups={groups}
                symbols={коды}
                symbolsFree={symbolsFree}
                canEdit={canEdit}
                onChange={(patch) => change(d.key, patch)}
                onRemove={() => remove(d.key)}
              />
            ))}
        </LevelBlock>
      ))}

      <section className="block">
        <h3>Сохранение</h3>

        {повторы.length > 0 && (
          <div className="incomplete">
            <strong>Область действия задана дважды:</strong>
            <ul>
              {повторы.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <p className="muted">
              Два правила на одно сочетание — это не «побеждает последнее», а
              неопределённость, которой в матрице приоритетов быть не может.
            </p>
          </div>
        )}

        {смены.length === 0 ? (
          <p className="muted">
            Изменений нет. Сохранение без фактических смен снепшотов не пишет —
            история расчёта не изменится.
          </p>
        ) : (
          <div className="incomplete">
            <strong>
              Правка уйдёт в ИСТОРИЮ расчёта: {смены.length}{" "}
              {смены.length === 1 ? "снепшот" : "снепшота(-ов)"}.
            </strong>
            <ul>
              {смены.map((c) => (
                <li key={`${c.kind}:${c.scope}`}>
                  {SNAPSHOT_KINDS[c.kind]}: {c.scope}
                  {c.from !== null && c.to !== null
                    ? ` — A-Book ${c.from} % → ${c.to} %`
                    : c.to !== null
                      ? ` — A-Book ${c.to} %`
                      : ` — было A-Book ${c.from} %`}
                </li>
              ))}
            </ul>
            <p className="muted">
              Каждая смена записывается снепшотом перекрытия той же транзакцией,
              что и правило. По снепшотам считается РЕТРОСПЕКТИВНАЯ раскладка:
              смена закрывает один интервал и открывает другой, и правка меняет
              не только сегодняшнюю цифру, но и результат прошлых периодов.
            </p>
          </div>
        )}

        {confirm && (
          <div className="notice">
            Подтвердите: перечисленные выше смены будут записаны в историю
            перекрытия и изменят раскладку прошлых периодов. Отменить их можно
            будет только встречной правкой — снепшот не удаляется.
          </div>
        )}

        {saveError && <div className="error">{saveError}</div>}
        {notice && <p className="muted">{notice}</p>}

        <div className="inline">
          <button
            type="button"
            disabled={!canEdit || saving || смены.length === 0}
            onClick={save}
          >
            {saving
              ? "Сохраняем…"
              : confirm
                ? "Записать смены в историю"
                : "Сохранить матрицу"}
          </button>
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={() => {
              // Ответ сервера целиком, а не только его правила: в нём же
              // лежит признак права, и обёртка { rules: … } потеряла бы его.
              // Перезапроса нет намеренно — «отменить правку» означает
              // вернуть ДЕЙСТВУЮЩЕЕ положение, а не то, что на сервере стало
              // за время правки: второе было бы другой операцией.
              apply(server);
              setSaveError("");
              setNotice("");
            }}
          >
            Отменить правку
          </button>
        </div>

        <p className="muted">
          Сохранение отправляет матрицу ЦЕЛИКОМ: правило, которого нет в
          черновике, будет снято, и снятие тоже уйдёт в историю — после него
          начинает действовать уровень ниже.
        </p>
      </section>
    </div>
  );
}
